<template>
  <table
    class="table hera-table m-0 px-0 sort-table"
    :class="headerPosition ? `table-${headerPosition}` : null"
  >
    <thead class="thead-main">
      <slot name="theadHelperRow" />
      <tr class="cursor-pointer text-uppercase">
        <template
          v-for="({ id, title, sortProp, width, notSort }, index) in columns"
        >
          <th
            v-if="notSort"
            :key="`th_${id}`"
            scope="col"
            :style="width ? { width } : null"
          >
            <TextLabel :text="title" />
          </th>
          <th
            v-else
            :key="`th_${id}`"
            :class="{ 'text-light': sortById === id }"
            scope="col"
            :style="width ? { width } : null"
            @click="sortBy(id, sortProp)"
          >
            <TextLabel :text="title" />
            <Icon
              v-if="icons[index]"
              :name="icons[index]"
              :fill="$scss.colors.secondaryTextBg"
              :hover-fill="$scss.colors.primaryTextBg"
            />
          </th>
        </template>
      </tr>
    </thead>
    <thead v-if="headerPosition === 'pseudo-fixed'" class="invisible">
      <tr class="text-uppercase">
        <th
          v-for="({ id, title, width, notSort }, index) in columns"
          :key="`th_shadow_${id}`"
          scope="col"
          :style="width ? { width } : null"
        >
          {{ title }}
          <Icon v-if="icons[index] && !notSort" :name="icons[index]" />
        </th>
      </tr>
    </thead>
    <tbody>
      <slot name="tbodyHelperRow" />
      <tr
        v-for="(row, rowIndex) in rowsSorted"
        :key="`tr_${row.id}`"
        :data-id="row.id"
        v-bind="{ ...$lodash.get(row, 'binds.tr', null) }"
        v-on="listenersComputed(row).th || null"
      >
        <td
          v-for="({ id, defaultValue }, index) in columns"
          :key="`td_${id}`"
          v-bind="{ scope: index ? null : 'row' }"
        >
          <slot :name="id" v-bind="{ data: row[id], row, rowIndex }">{{
            row[id] !== null ? row[id] : defaultValue
          }}</slot>
        </td>
      </tr>
    </tbody>
    <tfoot v-if="tableLoading">
      <tr>
        <td colspan="100%" class="sort-table-loader block-loading"></td>
      </tr>
    </tfoot>
  </table>
</template>

<script>
import Icon from '@/components/atoms/Icon';
import { prop, propRequired, emptyObject } from '@/utils/factories';
import TextLabel from '@/components/atoms/TextLabel';
export default {
  name: 'SortTable',
  components: {
    Icon,
    TextLabel,
  },
  props: {
    /**
     * columns: [{ id, title, sortProp, width, sortInNumber, sortInUnicode, defaultValue, notSort }, ...]
     */
    columns: propRequired(Array),
    rows: propRequired(Array),
    sortInComponent: prop(Boolean),
    sortIsDisabled: prop(Boolean),
    tableLoading: prop(Boolean),
    headerPosition: prop(String),
    listeners: prop(Object),
  },
  data() {
    return {
      sortById: '',
      sortByProp: '',
      order: 0,
      rowsSorted: this.rows,
    };
  },
  computed: {
    icons() {
      return this.columns.map(
        ({ id }) =>
          `arrow-${this.sortById !== id ? 't-b' : this.order === 1 ? 'b' : 't'}`
      );
    },
  },
  watch: {
    rows(val) {
      if (this.sortInComponent) {
        this.rowsSorted = this.rowsSort(val);
      }
    },
  },
  methods: {
    sortBy(id, prop = null, order = 0) {
      if (this.sortIsDisabled) {
        return;
      }
      if (this.sortById === id) {
        this.order = order || this.order * -1; // reverse
      } else {
        this.sortById = id;
        if (prop !== -1) {
          this.sortByProp = prop;
        }
        this.order = order || 1;
      }

      this.$emit('sort', { sortBy: id, order: this.order });
      if (this.sortInComponent) {
        this.rowsSorted = this.rowsSort(this.rows);
      }
    },
    rowsSort(rows) {
      const sorted = this.$lodash.sortBy(rows, [
        this.sorting,
        (row, index) => this.$lodash.get(this.columns, '[0].id', index),
      ]);

      if (this.order < 0) {
        sorted.reverse();
      }

      return sorted;
    },
    sorting(row) {
      const definer = this.sortByProp
        ? this.$lodash.get(row[this.sortById], `${this.sortByProp}`)
        : row[this.sortById];

      const { sortInNumber = false, sortInUnicode = false } =
        this.columns.find(c => c.id === this.sortById) || emptyObject();

      if (sortInNumber) {
        return definer === null ? this.order * Infinity : +definer;
      } else if (!sortInUnicode) {
        /* backend sorted only in 1 letter case (lower or|and upper) */
        return String(definer).toLowerCase();
      }

      return definer;
    },

    listenersComputed(row) {
      return Object.entries(this.listeners || {}).reduce((acc, [key, cbs]) => {
        acc[key] = Object.entries(cbs).reduce((eventsAcc, [eventName, cb]) => {
          eventsAcc[eventName] = (...args) => cb(row, ...args);
          return eventsAcc;
        }, {});
        return acc;
      }, {});
    },
  },
};
</script>

<style lang="scss">
.sort-table {
  tbody {
    tr {
      &:nth-child(odd) {
        background: rgba($ph-popover-bg-raw, 0.2);
      }
    }
  }
  &.table-pseudo-fixed {
    .thead-main {
      position: absolute;
      z-index: 44;
      top: 0;
      left: 0;
      right: 0;

      /*@include auto-rtl(
        background,
        linear-gradient(
          to right,
          $element-bg-color 0%,
          $element-bg-color calc(100% - 4px),
          transparent calc(100% - 4px),
          transparent 100%
        )
      );*/
    }
  }
  &.table-fixed {
    .thead-main {
      position: fixed;
      z-index: 44;
    }
  }
  &-loader {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 4rem;
    &:after {
      left: 2rem;
    }
  }
}
</style>
